/**
 * Public `/signup/report#token=…` — the "This wasn't us" link in the email
 * sent to the person an AI agent named when it signed an organisation up
 * (migration 080). No sign-in: the token in the link is the authority.
 *
 * The token lives in the URL FRAGMENT, so it never reaches a server log or a
 * Referer. The page only previews on load; reporting is a button the person
 * presses, because mail scanners open links and must not report anything.
 * Reporting switches the organisation's widget keys off, revokes its AI agent
 * access and flags it for FinderlyFix. Nothing is deleted.
 */
import { useEffect, useState } from 'react';
import type { SignupReportPreview, SignupReportResult } from '../api/types';
import { useSession } from '../auth/session';
import { Link } from '../router';
import { ErrorNotice, Loading, formatDateTime } from '../ui/kit';
import { ApiError } from '../api/errors';

export function reportTokenFromHash(hash: string): string | null {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const token = params.get('token')?.trim();
  return token ? token : null;
}

export function SignupReport() {
  const { api } = useSession();
  const [token] = useState(() => reportTokenFromHash(window.location.hash));
  const [preview, setPreview] = useState<SignupReportPreview | null>(null);
  const [previewError, setPreviewError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(Boolean(token));
  const [result, setResult] = useState<SignupReportResult | null>(null);
  const [claimed, setClaimed] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [reportError, setReportError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    api
      .previewSignupReport(token)
      .then((p) => !cancelled && setPreview(p))
      .catch((err: unknown) => !cancelled && setPreviewError(err instanceof ApiError ? err : null))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [api, token]);

  let body: React.ReactNode;
  if (!token) {
    body = (
      <div className="notice bad" role="alert">
        <span>This link is incomplete. Open the “This wasn’t us” link from the email again, or contact support@finderlyfix.com.</span>
      </div>
    );
  } else if (loading) {
    body = <Loading label="Checking the link" />;
  } else if (previewError || !preview) {
    body = <ErrorNotice error={previewError ?? new ApiError({ status: 404, code: 'SIGNUP_REPORT_LINK_INVALID' })} />;
  } else if (claimed || preview.claimed) {
    body = (
      <div className="notice info" role="status" data-testid="report-claimed">
        <span>
          <strong>This account has been claimed.</strong> The person the AI agent named confirmed it, so this link no longer does
          anything. If you think that is wrong, contact support@finderlyfix.com.
        </span>
      </div>
    );
  } else if (result || preview.reported) {
    body = (
      <div className="notice ok" role="status">
        <span>
          <strong>{result?.already_reported || (!result && preview.reported) ? 'Already reported.' : 'Reported.'}</strong>{' '}
          {result?.message ??
            'The account’s widget keys are switched off and its AI agent access is revoked. FinderlyFix will look into it. Nothing was deleted.'}
        </span>
      </div>
    );
  } else {
    body = (
      <div className="stack-sm">
        <p>An AI agent created a FinderlyFix partner account and named you as the person who authorised it:</p>
        <dl className="facts" data-testid="report-facts">
          <dt>Organisation</dt>
          <dd>{preview.organisation}</dd>
          <dt>Website</dt>
          <dd>{preview.origin}</dd>
          <dt>Created</dt>
          <dd>{formatDateTime(preview.created_at)}</dd>
        </dl>
        <p>
          If you did not authorise it, report it. Its widget keys are switched off at once, its AI agent access is revoked, and
          FinderlyFix looks into it. Nothing is deleted.
        </p>
        <ErrorNotice error={reportError} />
        <div className="row">
          <button
            type="button"
            className="btn btn-danger"
            disabled={reporting}
            onClick={async () => {
              setReporting(true);
              setReportError(null);
              try {
                setResult(await api.reportSignup(token ?? ''));
              } catch (err) {
                // Claimed in the meantime: the link does nothing any more.
                if (err instanceof ApiError && err.code === 'SIGNUP_ALREADY_CLAIMED') setClaimed(true);
                else setReportError(err instanceof ApiError ? err : new ApiError({ status: 500, code: 'INTERNAL_ERROR' }));
              } finally {
                setReporting(false);
              }
            }}
          >
            {reporting ? 'Reporting…' : 'Report it: this wasn’t us'}
          </button>
        </div>
        <p className="small muted">
          If you did authorise it, you don’t need this page: <Link to="/claim">sign in and claim the account</Link> instead.
        </p>
      </div>
    );
  }

  return (
    <main className="shared-dashboard legal-page" id="main">
      <header className="row-between legal-head">
        <Link to="/" className="eyebrow legal-home">
          FinderlyFix Partners
        </Link>
      </header>
      <article className="stack" aria-labelledby="report-title">
        <h1 id="report-title">This wasn’t us</h1>
        {body}
      </article>
    </main>
  );
}
